import { ChangeEvent, useCallback, useMemo, useState } from "react";
import Fuse from "fuse.js";
import { ProjectQuery } from "@gql/sites-api/graphql";
import { isDefined } from "react-migration/lib/util/isDefined";

type ProjectFromQuery = NonNullable<ProjectQuery["project"]>;

const SEARCHABLE_PROJECT_FIELDS: (keyof ProjectFromQuery)[] = ["title", "goal", "developmentType"];

interface UseProjectSearchProps {
  list: ProjectFromQuery[];
  selected: ProjectFromQuery | null | undefined;
}

export function useProjectSearch({ list, selected }: UseProjectSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const projectSearchIndex = useMemo(() => {
    const allProjects = [selected, ...list].filter(isDefined);

    if (!allProjects.length) return null;

    return new Fuse(allProjects, {
      ignoreLocation: true,
      isCaseSensitive: false,
      threshold: 0.3,
      keys: SEARCHABLE_PROJECT_FIELDS,
    });
  }, [selected, list]);

  const filteredList = useMemo(() => {
    if (!searchQuery.trim() || !projectSearchIndex) {
      return [selected, ...list].filter(isDefined);
    }

    return projectSearchIndex
      .search(searchQuery)
      .map((result) => result.item)
      .sort((a, b) => {
        // if selected project matches, move it to the top
        if (selected?.id === a.id) return -1;
        if (selected?.id === b.id) return 1;
        return 0;
      });
  }, [searchQuery, projectSearchIndex, selected, list]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  return {
    searchQuery,
    filteredList,
    handleSearchChange,
  };
}
